import { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { localize } from '@oneflowab/pomes';
import { useSelector, useDispatch } from 'react-redux';
import urlJoin from 'url-join';
import { Redirect } from 'react-router';
import workspacesReducer from '@app/reducers/entities/workspaces';
import type { Match } from 'react-router-dom';

import { checkAcl } from '@flow/libs/utils/acl';
import type { MessageTranslator } from '@oneflowab/pomes';

import adminPage from '@app/hocs/admin-page';
import { AdminPageComponentProps } from 'types/admin-page-props';

import WorkspaceDetails from './details';
import WorkspaceAccess from './access';
import WorkspaceBranding from './branding';
import WorkspaceAiReview from './ai-review';
import { AutomationRules } from './automation-rules';

export type Props = AdminPageComponentProps & {
  match: Match;
};

export const WorkspaceComponent = (props: Props) => {
  const { match } = props;
  return (
    <Redirect
      from={match.path}
      to={urlJoin(match.url, '/details')}
    />
  );
};

type PropsWithMessage = {
  message: MessageTranslator;
  workspace: Oneflow.Workspace;
};

export const propsMapper = ({ props: { message, workspace } }: { props: PropsWithMessage }) => {
  const hasBrandingAccess = checkAcl(workspace.acl, 'collection:update:branding');
  const hasDataRetentionAccess = checkAcl(workspace.acl, 'collection:data_retention_policy:update');
  const hasAiReviewAccess = checkAcl(workspace.acl, 'collection:ai_review:admin');

  const getWorkspaceModules = () => {
    const workspaceModules = [
      {
        path: '/details',
        title: message({
          id: 'Details',
          comment: 'Tab title for the workspace details page.',
        }),
        section: message({
          id: 'Workspaces',
          comment: 'Used as the title for the section.',
        }),
        component: WorkspaceDetails,
      },
      {
        path: '/access',
        title: message({
          id: 'Access',
          comment: 'Tab title for the workspace access page.',
        }),
        section: message({
          id: 'Workspaces',
          comment: 'Used as the title for the section.',
        }),
        component: WorkspaceAccess,
      },
    ];

    if (hasBrandingAccess) {
      workspaceModules.push({
        path: '/branding',
        title: message({
          id: 'Branding',
          comment: 'Tab title for the workspace branding page.',
        }),
        section: message({
          id: 'Workspaces',
          comment: 'Used as the title for the section.',
        }),
        component: WorkspaceBranding,
      });
    }

    if (hasDataRetentionAccess) {
      workspaceModules.push({
        path: '/data-retention', // TODO: Rename to automation-rules when ready
        title: message({
          id: 'Data retention', // TODO: Rename to Automation Rules when ready
          comment: 'Tab title for the workspace data retention page.',
        }),
        section: message({
          id: 'Workspaces',
          comment: 'Used as the title for the section.',
        }),
        component: AutomationRules,
      });
    }

    if (hasAiReviewAccess) {
      workspaceModules.push({
        path: '/ai-review',
        title: 'AI Review',
        section: message({
          id: 'Workspaces',
          comment: 'Used as the title for the section.',
        }),
        component: WorkspaceAiReview,
        isBeta: true,
        event: {
          event: 'Go To AI Review Playbook',
          props: {
            'workspace id': workspace.id,
            location: 'workspace details page - tab',
          },
        },
      });
    }

    return workspaceModules;
  };

  return {
    title: workspace.name || '',
    showAsLink: false,
    modules: [
      getWorkspaceModules(),
    ],
  };
};
export const AdminWorkspaceComponent = adminPage(propsMapper)(WorkspaceComponent);

export const WorkspacePage = (props: Props) => {
  const { match } = props;
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      workspacesReducer.fetchWorkspace({
        id: match.params.id,
        params: {
          accessStats: 1,
          includeAgreementStats: 1,
        },
      }),
    );
  }, [
    dispatch,
    match.params.id,
  ]);

  const workspace = useSelector((state: State) =>
    workspacesReducer.getWorkspaceSelector(state, {
      id: match.params.id,
    }),
  );

  if (isEmpty(workspace)) {
    return null;
  }

  return (
    <AdminWorkspaceComponent
      {...props}
      workspace={workspace}
    />
  );
};

export const LocalizedWorkspacePage = localize(WorkspacePage);
